import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Alert, Box, Button, Select, Spinner, TextField, Typography } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { adminApiClient, AdminApiError, type RunnerLimitRecordPack, type RunnerLimitRequestRow } from '../lib/adminApiClient';
import { NEUTRAL_BTN, DANGER_BTN } from '../lib/buttonStyles';
import { Pill } from '../components/Pill';
import { METRO_LABEL, TRANSPORT_LABEL } from '../lib/runnerChips';

/**
 * The one-screen record pack (Figma admin frame `1793:120`): who the Runner
 * is, what they have and use, reliability, money flags, the limits history —
 * then the decision on a request, and "Set limits by hand" for any Runner.
 *
 * 🔒 Every decision and every override needs a REASON: it is emailed to the
 * Runner verbatim and lands in the audit ledger. The form refuses without one.
 * 🔒 A grant above the top tier is clamped server-side; the form says so.
 */
const STAGE_LABEL: Record<string, string> = { starting: 'Starting line (15/wk · 5 hh)', trusted: 'Trusted (40/wk · 15 hh)', trusted90: 'Trusted 90 days (no delivery ceiling · 30 hh)' };
const TOP = { deliveries: '∞', households: 30 };

function fmt(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}
function ceiling(n: number | null): string {
  return n === null ? 'no ceiling' : String(n);
}

export default function RunnerLimitReview() {
  const { id, runnerId } = useParams();
  const caps = useCapabilities();
  const canDecide = caps.has('runner:limits:review');
  const navigate = useNavigate();
  const [pack, setPack] = useState<RunnerLimitRecordPack | null>(null);
  const [request, setRequest] = useState<RunnerLimitRequestRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);
  const [busy, setBusy] = useState(false);
  // decision form
  const [reason, setReason] = useState('');
  const [grantD, setGrantD] = useState('');
  const [grantH, setGrantH] = useState('');
  const [expires, setExpires] = useState('');
  const [lang, setLang] = useState<'en' | 'es'>('en');
  // override form
  const [ovD, setOvD] = useState('');
  const [ovH, setOvH] = useState('');
  const [ovReason, setOvReason] = useState('');
  const [ovExpires, setOvExpires] = useState('');
  const [ovOpen, setOvOpen] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      if (id) {
        const res = await adminApiClient.getRunnerLimitRequest(id);
        setRequest(res.request);
        setPack(res);
        setGrantD(res.request.requestedDeliveries !== null ? String(res.request.requestedDeliveries) : '');
        setGrantH(res.request.requestedHouseholds !== null ? String(res.request.requestedHouseholds) : '');
      } else if (runnerId) {
        setRequest(null);
        setPack(await adminApiClient.getRunnerLimits(runnerId));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the record');
    }
  }, [id, runnerId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (caps.loading || (!pack && !error)) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><Spinner color="brand" /></Box>;
  if (error || !pack) return <Alert severity="error">{error ?? 'Not found'}</Alert>;

  const r = pack.runner;
  const overridden = pack.limits.source.deliveries === 'override' || pack.limits.source.households === 'override';
  const toIso = (ymd: string) => (ymd ? new Date(`${ymd}T23:59:59Z`).toISOString() : null);

  async function decide(decision: 'approve' | 'decline') {
    if (!request) return;
    setBusy(true);
    setFlash(null);
    try {
      const res = await adminApiClient.decideRunnerLimitRequest(request.id, {
        decision,
        reason: reason.trim(),
        lang,
        ...(decision === 'approve' ? { grantedDeliveries: grantD === '' ? null : Number(grantD), grantedHouseholds: grantH === '' ? null : Number(grantH), expiresAt: toIso(expires) } : {}),
      });
      setFlash({ kind: 'success', msg: decision === 'approve' ? `Approved — now ${ceiling(res.limits?.deliveries ?? null)}/wk · ${res.limits?.households ?? '—'} households. The Runner has been emailed.` : 'Declined. The Runner has been emailed and can ask again in 14 days.' });
      setReason('');
      await load();
    } catch (err) {
      setFlash({ kind: 'error', msg: err instanceof AdminApiError ? err.message : 'Could not record the decision.' });
    } finally {
      setBusy(false);
    }
  }

  async function override() {
    setBusy(true);
    setFlash(null);
    try {
      const body = {
        reason: ovReason.trim(),
        lang,
        ...(ovD !== '' ? { deliveries: ovD === 'clear' ? null : Number(ovD) } : {}),
        ...(ovH !== '' ? { households: ovH === 'clear' ? null : Number(ovH) } : {}),
        expiresAt: toIso(ovExpires),
      };
      const res = await adminApiClient.overrideRunnerLimits(r.id, body);
      setFlash({ kind: 'success', msg: `Limits set — ${ceiling(res.limits.deliveries)}/wk · ${res.limits.households} households. The Runner has been emailed.` });
      setOvOpen(false);
      setOvD(''); setOvH(''); setOvReason(''); setOvExpires('');
      await load();
    } catch (err) {
      setFlash({ kind: 'error', msg: err instanceof AdminApiError ? err.message : 'Could not set the limits.' });
    } finally {
      setBusy(false);
    }
  }

  const pending = request?.status === 'pending';

  return (
    <Box>
      <Button ariaLabel="Back to Runner limits" buttonText="‹ Runner limits" variant="text" size="small" onClick={() => navigate('/runner-limits')} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 1, mb: 0.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>{r.name}</Typography>
        {r.glpdId && <Pill label={r.glpdId} fg="#ffffff" bg="#2E9E3A" strong />}
        <Pill label={STAGE_LABEL[pack.stage] ?? pack.stage} fg="#343F5B" bg="#e8ebf2" />
        {overridden && <Pill label="Set by hand" fg="#9a3412" bg="#ffedd5" strong />}
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {r.email} · {METRO_LABEL[r.metro] ?? r.metro} · {TRANSPORT_LABEL[r.transportMode] ?? r.transportMode} · live since {fmt(r.wentLiveAt)}
        {r.trustedSince ? ` · Trusted since ${fmt(r.trustedSince)}` : ''}
      </Typography>
      {flash && <Box sx={{ mb: 2 }}><Alert severity={flash.kind}>{flash.msg}</Alert></Box>}

      {request && (
        <Panel title={pending ? 'The ask' : `The ask — ${request.status} ${fmt(request.decidedAt)}`}>
          <Typography sx={{ fontWeight: 700, color: '#1f2937' }}>
            {request.requestedDeliveries !== null ? `${request.requestedDeliveries} deliveries a week` : ''}
            {request.requestedDeliveries !== null && request.requestedHouseholds !== null ? ' and ' : ''}
            {request.requestedHouseholds !== null ? `${request.requestedHouseholds} households` : ''}
            <Typography component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}> · asked {fmt(request.createdAt)}</Typography>
          </Typography>
          {request.note && <Typography sx={{ color: '#374151', mt: 0.5, fontStyle: 'italic' }}>“{request.note}”</Typography>}
          {request.decisionReason && <Typography sx={{ color: '#374151', mt: 1 }}><b>Reason given:</b> {request.decisionReason}</Typography>}
        </Panel>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
        <Panel title="Has · using">
          <Field label="Deliveries a week" value={`${pack.usage.weeklyCapacity} offered of ${ceiling(pack.limits.deliveries)}${pack.limits.source.deliveries === 'override' ? ' (set by hand)' : ''}`} />
          <Field label="Households" value={`${pack.usage.activeHouseholds} of ${pack.limits.households}${pack.limits.source.households === 'override' ? ' (set by hand)' : ''}`} />
          <Field label="Deliveries completed" value={String(pack.usage.deliveriesCompleted)} />
          <Field label="Rating" value={r.ratingAvg !== null ? `${r.ratingAvg.toFixed(2)} from ${r.ratingCount}` : 'none yet'} />
          {overridden && (
            <Box sx={{ mt: 1, p: 1.5, borderRadius: 2, backgroundColor: '#fff7ed' }}>
              <Typography variant="body2" sx={{ color: '#9a3412' }}>
                Set by {pack.limits.overrideBy ?? 'a person'} on {fmt(pack.limits.overrideAt)}{pack.limits.overrideExpiresAt ? `, until ${fmt(pack.limits.overrideExpiresAt)}` : ''}: “{pack.limits.overrideReason}”
              </Typography>
            </Box>
          )}
        </Panel>
        <Panel title="Reliability · money">
          <Field label="Accepted, not delivered" value={String(pack.reliability.acceptedNotDelivered)} warn={pack.reliability.acceptedNotDelivered > 0} />
          <Field label="Exceptions, 90 days" value={String(pack.reliability.exceptions90d)} warn={pack.reliability.exceptions90d > 2} />
          <Field label="Households ended, 90 days" value={String(pack.reliability.householdsEnded90d)} warn={pack.reliability.householdsEnded90d > 1} />
          <Field label="Receipt variance flags" value={String(pack.money.varianceFlags)} warn={pack.money.varianceFlags > 0} />
        </Panel>
      </Box>

      {pack.history.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Panel title="Limits history">
            {pack.history.map((h, i) => (
              <Typography key={i} variant="body2" sx={{ color: '#374151', py: 0.25 }}>
                {fmt(h.at)} · <b>{h.action.replace('limits.', '')}</b> · {h.by ?? '—'}{h.detail && typeof h.detail === 'object' && 'reason' in (h.detail as object) ? ` · “${String((h.detail as { reason?: string }).reason)}”` : ''}
              </Typography>
            ))}
          </Panel>
        </Box>
      )}

      {canDecide && pending && request && (
        <Box sx={{ mt: 2 }}>
          <Panel title="Decision">
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
              The reason is emailed to the Runner word for word. A grant above the top tier ({TOP.deliveries}/wk · {TOP.households} households) is brought down to it.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
              <Box><TextField label="Grant deliveries / wk" ariaLabel="Grant deliveries a week" size="small" type="number" fullWidth value={grantD} disabled={busy} onChange={(e) => setGrantD(e.target.value)} placeholder="leave as is" /></Box>
              <Box><TextField label="Grant households" ariaLabel="Grant households" size="small" type="number" fullWidth value={grantH} disabled={busy} onChange={(e) => setGrantH(e.target.value)} placeholder="leave as is" /></Box>
              <Box><TextField label="Until (optional)" ariaLabel="Grant expires on" size="small" type="date" fullWidth value={expires} disabled={busy} onChange={(e) => setExpires(e.target.value)} /></Box>
              <Box><Select ariaLabel="Email language" label="Email language" native size="small" value={lang} onChange={(_e, v) => setLang(v as 'en' | 'es')} options={[{ value: 'en', label: 'English' }, { value: 'es', label: 'Español' }]} sx={{ width: '100%' }} /></Box>
            </Box>
            <Box sx={{ mt: 2 }}>
              <TextField label="Reason (the Runner reads this)" ariaLabel="Reason" size="small" fullWidth multiline rows={3} value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
              <Button ariaLabel="Approve" buttonText={busy ? 'Working…' : 'Approve'} variant="contained" size="small" disabled={busy || reason.trim().length < 3 || (grantD === '' && grantH === '')} onClick={() => void decide('approve')} />
              <Button ariaLabel="Decline" buttonText="Decline" variant="outlined" size="small" sx={DANGER_BTN} disabled={busy || reason.trim().length < 3} onClick={() => void decide('decline')} />
            </Box>
          </Panel>
        </Box>
      )}

      {canDecide && (
        <Box sx={{ mt: 2 }}>
          <Panel title="Set limits by hand">
            {!ovOpen ? (
              <Button ariaLabel="Set limits by hand" buttonText="Set limits by hand" variant="outlined" size="small" sx={NEUTRAL_BTN} onClick={() => setOvOpen(true)} />
            ) : (
              <>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                  Either number, up or down, with a reason the Runner reads. "Clear" removes a hand-set number and the tier applies again. An expiry hands it back to the tier on that date.
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                  <Box>
                    <TextField label="Deliveries / wk" ariaLabel="Deliveries a week" size="small" fullWidth value={ovD} disabled={busy} onChange={(e) => setOvD(e.target.value)} placeholder={`now ${ceiling(pack.limits.deliveries)} · number or "clear"`} />
                  </Box>
                  <Box>
                    <TextField label="Households" ariaLabel="Households" size="small" fullWidth value={ovH} disabled={busy} onChange={(e) => setOvH(e.target.value)} placeholder={`now ${pack.limits.households} · number or "clear"`} />
                  </Box>
                  <Box><TextField label="Until (optional)" ariaLabel="Override expires on" size="small" type="date" fullWidth value={ovExpires} disabled={busy} onChange={(e) => setOvExpires(e.target.value)} /></Box>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <TextField label="Reason (the Runner reads this)" ariaLabel="Override reason" size="small" fullWidth multiline rows={3} value={ovReason} disabled={busy} onChange={(e) => setOvReason(e.target.value)} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
                  <Button ariaLabel="Save limits" buttonText={busy ? 'Working…' : 'Save limits'} variant="contained" size="small" disabled={busy || ovReason.trim().length < 3 || (ovD === '' && ovH === '') || (ovD !== '' && ovD !== 'clear' && !/^\d+$/.test(ovD)) || (ovH !== '' && ovH !== 'clear' && !/^\d+$/.test(ovH))} onClick={() => void override()} />
                  <Button ariaLabel="Cancel" buttonText="Cancel" variant="text" size="small" disabled={busy} onClick={() => setOvOpen(false)} />
                </Box>
              </>
            )}
          </Panel>
        </Box>
      )}
    </Box>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, backgroundColor: '#fff' }}>
      <Typography sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
      {children}
    </Box>
  );
}
function Field({ label, value, warn = false }: { label: string; value: string; warn?: boolean }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5, borderBottom: '1px solid #f1f5f9' }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color: warn ? '#b42318' : '#1f2937', textAlign: 'right' }}>{value}</Typography>
    </Box>
  );
}
